/**
 * SafetyPanel — Admin Control Room for Driver Emergency / SOS (Phase 1).
 *
 * Read/act surface for SOS events. Dispatcher actions: call (click-to-call),
 * SMS the driver, view ride, view live location (Google Maps link), resolve
 * with notes. Append-only audit + location history shown in the detail view.
 * Advisory only — touches no dispatch/ride state.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  ShieldAlert, Phone, MessageSquare, MapPin, Car, User, Clock, CheckCircle2,
  Loader2, RefreshCw, X, Send, History, FileText, ShieldCheck,
  Users, BellRing, AlertTriangle, Navigation,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { IncidentReview } from "./IncidentReview";
import { IncidentRegister } from "./IncidentRegister";
import { SafetyReports } from "./SafetyReports";
import { SosSettings } from "./SosSettings";

const DISCLAIMER = "This is an operator alerting tool, not a 999/emergency-services dispatch system. In a life-threatening emergency, call 999.";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB") : "—");
const mapsLink = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;
// Driver quick-replies that signal an all-clear → surface a one-click resolve.
const ALL_CLEAR = new Set(["I am safe.", "False alarm.", "Resolved."]);

const LevelBadge = ({ level, state }) => {
  const emergency = level === "emergency";
  const cls = state === "Resolved" ? "border-[#10B981] text-[#065F46] bg-[#ECFDF5]"
    : emergency ? "border-[#DC2626] text-[#991B1B] bg-[#FEE2E2]" : "border-[#F59E0B] text-[#92400E] bg-[#FEF3C7]";
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 border rounded ${cls}`}>{state}</span>;
};

export const SafetyPanel = ({ sosSignal = null, openSosId = null, onConsumeOpen = () => {} } = {}) => {
  const [events, setEvents] = useState(null);
  const [scope, setScope] = useState("active");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resolveNotes, setResolveNotes] = useState("");
  const [msg, setMsg] = useState("");
  const [reviewId, setReviewId] = useState(null);
  const [view, setView] = useState("board");
  const [standDownConfirm, setStandDownConfirm] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [notifying, setNotifying] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/sos?scope=${scope}`);
      setEvents(data.events || []);
    } catch (e) { toast.error(formatApiError(e) || "Failed to load SOS events"); }
  }, [scope]);

  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);

  const detailId = detail?.id;
  const refreshDetail = useCallback(async () => {
    if (!detailId) return;
    try { const { data } = await api.get(`/admin/sos/${detailId}`); setDetail(data); } catch { /* best-effort */ }
  }, [detailId]);

  // Deep-link: when an SOS alert overlay's "Open SOS" is clicked, jump to the
  // board and open that incident directly (no scrolling/searching needed).
  useEffect(() => {
    if (!openSosId) return;
    setView("board");
    openDetail(openSosId);
    onConsumeOpen();
  }, [openSosId]); // eslint-disable-line react-hooks/exhaustive-deps

  // WS-driven refresh (Admin.jsx bumps `sosSignal` on any sos_alert) — same
  // pattern as ride chat. Refreshes the board + the open detail instantly,
  // backed by the conservative poll below as a safety net.
  useEffect(() => {
    if (!sosSignal) return;
    load();
    if (detailId) refreshDetail();
  }, [sosSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-ish: while a detail drawer is open, poll it so driver replies + new
  // audit entries appear without reopening. (Admin.jsx already toasts on WS.)
  useEffect(() => {
    if (!detailId) return;
    const t = setInterval(refreshDetail, 8000);
    return () => clearInterval(t);
  }, [detailId, refreshDetail]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/sos/${id}`);
      setDetail(data); setResolveNotes(""); setMsg(""); setStandDownConfirm(false);
      setContacts([]); setNearby([]);
      // Phase 2 advisory data (best-effort, read-only).
      api.get(`/admin/sos/${id}/emergency-contacts`).then(({ data }) => setContacts(data.contacts || [])).catch(() => {});
      if (data.status === "active") {
        api.get(`/admin/sos/${id}/nearby-drivers`).then(({ data }) => setNearby(data.drivers || [])).catch(() => {});
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const notifyContacts = async () => {
    if (!window.confirm("Notify this driver's emergency contact(s) now? This sends an automated safety alert and is logged in the audit trail.")) return;
    setNotifying(true);
    try {
      const { data } = await api.post(`/admin/sos/${detail.id}/notify-contacts`, {});
      toast.success(`Notified ${data.notified} emergency contact(s).`);
      await refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); } finally { setNotifying(false); }
  };

  const messageDriver = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/sos/${detail.id}/message-driver`, { message: msg.trim() });
      toast.success(`Message sent to driver${data.sms_status === "sent" ? " (in-app + SMS)" : " (in-app)"}.`);
      setMsg(""); await refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const resolve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/sos/${detail.id}/resolve`, { notes: resolveNotes.trim() });
      toast.success("SOS marked resolved.");
      setDetail(null); await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="safety-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#DC2626]" /> Driver Safety / SOS</h2>
          <p className="text-xs text-muted-foreground">{DISCLAIMER}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border rounded-lg overflow-hidden text-xs">
            {[["board", "Live board"], ["register", "History register"], ["reports", "Auto reports"], ["settings", "Settings"]].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`sos-view-${v}`}
                className={`px-3 py-1.5 ${view === v ? "bg-[#0A0A0A] text-white" : "bg-white text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
          {view === "board" && (
            <>
              <div className="flex border border-border rounded-lg overflow-hidden text-xs">
                {["active", "all"].map((s) => (
                  <button key={s} onClick={() => setScope(s)} data-testid={`sos-scope-${s}`}
                    className={`px-3 py-1.5 capitalize ${scope === s ? "bg-[#0A0A0A] text-white" : "bg-white text-muted-foreground"}`}>{s}</button>
                ))}
              </div>
              <button onClick={load} className="p-2 border border-border rounded-lg hover:bg-secondary" data-testid="sos-refresh"><RefreshCw className="w-4 h-4" /></button>
            </>
          )}
        </div>
      </div>

      {view === "register" ? (
        <IncidentRegister onOpenIncident={setReviewId} />
      ) : view === "reports" ? (
        <SafetyReports />
      ) : view === "settings" ? (
        <SosSettings />
      ) : events === null ? (
        <div className="p-8 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : events.length === 0 ? (
        <div className="p-10 text-center text-sm text-muted-foreground border border-dashed border-border rounded-xl" data-testid="sos-empty">
          No {scope === "active" ? "active" : ""} SOS events. All quiet.
        </div>
      ) : (
        <div className="grid gap-3">
          {events.map((e) => {
            const emergency = e.level === "emergency";
            const isActive = e.status === "active";
            return (
              <div key={e.id} data-testid={`sos-card-${e.id}`}
                className={`rounded-xl border-2 p-4 ${isActive ? (emergency ? "border-[#DC2626] bg-[#FEF2F2] animate-pulse-slow" : "border-[#F59E0B] bg-[#FFFBEB]") : "border-border bg-white"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ShieldAlert className={`w-5 h-5 ${emergency ? "text-[#DC2626]" : "text-[#B45309]"}`} />
                      <span className="font-bold text-sm">{e.driver_name || "Driver"}</span>
                      <LevelBadge level={e.level} state={e.state} />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span className="flex items-center gap-1"><Car className="w-3 h-3" /> {e.vehicle?.make || "—"} · {e.vehicle?.plate || "—"}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(e.created_at)}</span>
                      {e.ride && <span className="flex items-center gap-1"><User className="w-3 h-3" /> On trip · {e.ride.passenger_name || "passenger"}</span>}
                    </div>
                  </div>
                  <button onClick={() => openDetail(e.id)} data-testid={`sos-open-${e.id}`}
                    className="text-xs font-semibold text-white bg-[#0A0A0A] hover:bg-black px-3 py-1.5 rounded-lg">Open</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail / action drawer */}
      {detail && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-3" onClick={() => setDetail(null)}>
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-auto rounded-2xl p-5" onClick={(ev) => ev.stopPropagation()} data-testid="sos-detail">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#DC2626]" /> {detail.driver_name}</h3>
                <div className="mt-1"><LevelBadge level={detail.level} state={detail.state} /></div>
              </div>
              <button onClick={() => setDetail(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-3 text-xs">
              <div className="border border-border rounded-lg p-2.5">
                <div className="label-mini text-muted-foreground mb-1">Driver & vehicle</div>
                <div>{detail.driver_phone || "no phone"}</div>
                <div className="text-muted-foreground">{detail.vehicle?.make || "—"} · {detail.vehicle?.plate || "—"}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5">
                <div className="label-mini text-muted-foreground mb-1">Current trip</div>
                {detail.ride ? (
                  <>
                    <div>{detail.ride.passenger_name} ({detail.ride.ride_status})</div>
                    <div className="text-muted-foreground">{detail.ride.pickup_address} → {detail.ride.dropoff_address}</div>
                  </>
                ) : <div className="text-muted-foreground">Not on a trip</div>}
              </div>
            </div>

            {/* Dispatcher actions */}
            <div className="flex flex-wrap gap-2 mt-3">
              {detail.driver_phone && (
                <a href={`tel:${detail.driver_phone}`} data-testid="sos-call-driver" className="text-xs font-semibold border border-border rounded-lg px-3 py-2 hover:bg-secondary flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Call driver</a>
              )}
              {(detail.last_lat != null) && (
                <a href={mapsLink(detail.last_lat, detail.last_lng)} target="_blank" rel="noreferrer" data-testid="sos-view-location" className="text-xs font-semibold border border-border rounded-lg px-3 py-2 hover:bg-secondary flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Live location</a>
              )}
              {detail.ride?.ride_id && (
                <span data-testid="sos-ride-ref" className="text-xs border border-border rounded-lg px-3 py-2 text-muted-foreground flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Ride {detail.ride.ride_id.slice(0, 8)}</span>
              )}
              <button onClick={() => setReviewId(detail.id)} data-testid="sos-review-btn"
                className="text-xs font-semibold border border-[#0A0A0A] rounded-lg px-3 py-2 hover:bg-secondary flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Incident Review</button>
            </div>

            {/* Escalation banner (advisory) */}
            {detail.escalated_at && detail.status === "active" && (
              <div className="mt-3 bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg p-2.5 flex items-center gap-2 text-xs text-[#991B1B]" data-testid="sos-escalation-banner">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span><b>Escalated</b> at {fmt(detail.escalated_at)} — no driver response within the configured window.</span>
              </div>
            )}

            {/* Emergency contacts (Phase 2) */}
            <div className="mt-3" data-testid="sos-emergency-contacts">
              <div className="label-mini text-muted-foreground mb-1 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Emergency contacts ({contacts.length})</div>
              {contacts.length === 0 ? (
                <div className="text-[11px] text-muted-foreground border border-dashed border-border rounded-lg p-2">No emergency contacts on file for this driver.</div>
              ) : (
                <div className="space-y-1 border border-border rounded-lg p-2">
                  {contacts.map((c) => (
                    <div key={c.id} className="text-xs flex items-center justify-between gap-2" data-testid={`sos-contact-${c.id}`}>
                      <span><b>{c.name}</b>{c.relationship ? ` · ${c.relationship}` : ""}{c.phone ? ` · ${c.phone}` : ""}{c.email ? ` · ${c.email}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 mt-1.5">
                <button onClick={notifyContacts} disabled={notifying || contacts.length === 0} data-testid="sos-notify-contacts-btn"
                  className="text-xs font-semibold bg-[#B45309] hover:bg-[#92400E] text-white rounded-lg px-3 py-1.5 disabled:opacity-50 flex items-center gap-1.5">
                  {notifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />} Notify emergency contacts
                </button>
                {detail.contacts_notified_at && <span className="text-[11px] text-[#065F46]" data-testid="sos-contacts-notified-at">Notified {fmt(detail.contacts_notified_at)}</span>}
              </div>
            </div>

            {/* Nearest available drivers (advisory, read-only) */}
            {detail.status === "active" && (
              <div className="mt-3" data-testid="sos-nearby-drivers">
                <div className="label-mini text-muted-foreground mb-1 flex items-center gap-1"><Navigation className="w-3.5 h-3.5" /> Nearest available drivers (advisory)</div>
                {nearby.length === 0 ? (
                  <div className="text-[11px] text-muted-foreground border border-dashed border-border rounded-lg p-2">No online, available drivers nearby right now.</div>
                ) : (
                  <div className="border border-border rounded-lg divide-y divide-border">
                    {nearby.map((d) => (
                      <div key={d.driver_id} className="text-xs flex items-center justify-between gap-2 px-2 py-1.5" data-testid={`sos-nearby-${d.driver_id}`}>
                        <span><b>{d.name || "Driver"}</b>{d.plate ? ` · ${d.plate}` : ""} · <span className="text-muted-foreground">{d.distance_km} km</span></span>
                        {d.phone && <a href={`tel:${d.phone}`} className="text-[#0A0A0A] font-semibold hover:underline flex items-center gap-1"><Phone className="w-3 h-3" /> Call</a>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">Advisory only — contacting a driver here does not assign or dispatch them.</div>
              </div>
            )}

            {/* Conversation thread (two-way in-app + SMS) */}
            <div className="mt-3">
              <div className="label-mini text-muted-foreground mb-1 flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Conversation with driver</div>
              {detail.messages?.length > 0 ? (
                <div className="max-h-44 overflow-y-auto space-y-1.5 border border-border rounded-lg p-2 mb-2" data-testid="sos-conversation">
                  {detail.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.from_role === "admin" ? "justify-end" : "justify-start"}`} data-testid={`sos-conv-msg-${m.from_role}`}>
                      <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-[12px] ${m.from_role === "admin" ? "bg-[#0A0A0A] text-white" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                        <div className="text-[9px] uppercase tracking-wide opacity-70 mb-0.5">{m.from_role === "admin" ? (m.from_name || "Control Room") : (detail.driver_name || "Driver")} · {fmt(m.ts)}</div>
                        {m.body}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground border border-dashed border-border rounded-lg p-2 mb-2">No messages yet.</div>
              )}
              <div className="flex gap-2">
                <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") messageDriver(); }} placeholder="Message the driver (in-app + SMS)…" data-testid="sos-message-input"
                  className="flex-1 border border-border rounded-lg px-3 py-2 text-sm" />
                <button onClick={messageDriver} disabled={busy || !msg.trim()} data-testid="sos-message-send"
                  className="text-xs font-semibold bg-[#0A0A0A] text-white rounded-lg px-3 py-2 disabled:opacity-50 flex items-center gap-1.5">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
                </button>
              </div>
            </div>

            {/* Location history */}
            {detail.location_history?.length > 0 && (
              <div className="mt-3">
                <div className="label-mini text-muted-foreground mb-1 flex items-center gap-1"><History className="w-3.5 h-3.5" /> Location history ({detail.location_history.length})</div>
                <div className="max-h-28 overflow-auto text-[11px] font-mono-tab border border-border rounded-lg p-2 space-y-0.5">
                  {detail.location_history.map((p, i) => (
                    <div key={i}>{fmt(p.ts)} — <a className="text-[#6D28D9] hover:underline" href={mapsLink(p.lat, p.lng)} target="_blank" rel="noreferrer">{p.lat?.toFixed(5)}, {p.lng?.toFixed(5)}</a></div>
                  ))}
                </div>
              </div>
            )}

            {/* Audit trail */}
            {detail.audit?.length > 0 && (
              <div className="mt-3">
                <div className="label-mini text-muted-foreground mb-1 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Audit trail</div>
                <div className="max-h-32 overflow-auto text-[11px] border border-border rounded-lg p-2 space-y-0.5" data-testid="sos-audit-trail">
                  {detail.audit.map((a) => (
                    <div key={a.id}><span className="text-muted-foreground">{fmt(a.ts)}</span> · <b>{a.action}</b>{a.actor_name ? ` by ${a.actor_name}` : ""}{a.detail ? ` — ${a.detail}` : ""}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Resolve */}
            {detail.status === "active" ? (
              <div className="mt-4 border-t border-border pt-3">
                {(() => {
                  const allClear = (detail.messages || []).filter((m) => m.from_role === "driver" && ALL_CLEAR.has(m.body)).slice(-1)[0];
                  if (!allClear) return null;
                  return (
                    <div className="mb-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg p-3" data-testid="sos-stand-down-suggest">
                      <div className="text-xs font-semibold text-[#065F46] flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4" /> Driver indicated all-clear: “{allClear.body}”
                      </div>
                      {!standDownConfirm ? (
                        <button onClick={() => { setStandDownConfirm(true); setResolveNotes((n) => n || `Driver reported: "${allClear.body}"`); }}
                          data-testid="sos-stand-down-resolve-btn"
                          className="mt-2 w-full text-sm font-semibold bg-[#065F46] hover:bg-[#064E3B] text-white rounded-lg py-2 flex items-center justify-center gap-1.5">
                          <ShieldCheck className="w-4 h-4" /> Stand Down &amp; Resolve
                        </button>
                      ) : (
                        <div className="mt-2 space-y-2" data-testid="sos-stand-down-confirm">
                          <div className="text-[11px] text-[#065F46]">Confirm stand-down. Edit the resolution notes if needed — the incident is marked Resolved only after you confirm.</div>
                          <textarea value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)} rows={2} data-testid="sos-stand-down-notes"
                            className="w-full border border-[#A7F3D0] rounded-lg px-3 py-2 text-sm" />
                          <div className="flex gap-2">
                            <button onClick={resolve} disabled={busy} data-testid="sos-stand-down-confirm-btn"
                              className="flex-1 text-sm font-semibold bg-[#065F46] hover:bg-[#064E3B] text-white rounded-lg py-2 flex items-center justify-center gap-1.5">
                              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirm Stand Down &amp; Resolve
                            </button>
                            <button onClick={() => setStandDownConfirm(false)} disabled={busy} data-testid="sos-stand-down-cancel"
                              className="text-sm font-semibold border border-border rounded-lg px-3 hover:bg-secondary">Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="label-mini text-muted-foreground mb-1">Resolve with notes</div>
                <textarea value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)} rows={2} data-testid="sos-resolve-notes"
                  placeholder="What happened and how it was resolved…" className="w-full border border-border rounded-lg px-3 py-2 text-sm" />
                <button onClick={resolve} disabled={busy} data-testid="sos-resolve-btn"
                  className="mt-2 w-full text-sm font-semibold bg-[#065F46] hover:bg-[#064E3B] text-white rounded-lg py-2 flex items-center justify-center gap-1.5">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Mark resolved
                </button>
              </div>
            ) : (
              <div className="mt-4 border-t border-border pt-3 text-xs text-[#065F46] bg-[#ECFDF5] rounded-lg p-2.5">
                Resolved by {detail.resolved_by} at {fmt(detail.resolved_at)}{detail.resolution_notes ? ` — ${detail.resolution_notes}` : ""}
              </div>
            )}
          </div>
        </div>
      )}

      {reviewId && <IncidentReview sosId={reviewId} onClose={() => setReviewId(null)} />}
    </div>
  );
};

export default SafetyPanel;
